import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FiCreditCard, FiSearch, FiX, FiSettings } from 'react-icons/fi';
import {
  getSuperAdminDerasars,
  getDerasarOnlinePayment,
  updateDerasarOnlinePayment,
} from '../../services/api';
import { getApiErrorMessage } from '../../services/apiError';
import styles from './OnlinePaymentSettings.module.css';

const EditModal = ({ derasar, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [keyId, setKeyId] = useState('');
  const [keySecret, setKeySecret] = useState('');
  const [hasSecret, setHasSecret] = useState(false);

  useEffect(() => {
    let active = true;
    getDerasarOnlinePayment(derasar.id)
      .then((res) => {
        if (!active) return;
        const d = res.data;
        setEnabled(!!d.onlinePaymentEnabled);
        setKeyId(d.razorpayKeyId || '');
        setHasSecret(!!d.hasRazorpayKeySecret);
      })
      .catch((err) => toast.error(getApiErrorMessage(err, 'Failed to load settings')))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [derasar.id]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { onlinePaymentEnabled: enabled };
      if (enabled) {
        payload.razorpayKeyId = keyId.trim();
        if (keySecret.trim()) payload.razorpayKeySecret = keySecret.trim();
      }
      await updateDerasarOnlinePayment(derasar.id, payload);
      toast.success('Online payment settings updated');
      onSaved();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHead}>
          <div>
            <h3>{derasar.name}</h3>
            {derasar.city && <div className={styles.sub}>{derasar.city}</div>}
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close"><FiX /></button>
        </div>

        {loading ? <p className={styles.muted}>Loading…</p> : (
          <>
            <div className={styles.statusRow}>
              <div>
                <div className={styles.fieldLabel}>Online Payment</div>
                <span className={`badge ${enabled ? 'badge-approved' : 'badge-rejected'}`}>
                  {enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <button
                type="button"
                className={`btn btn-sm ${enabled ? 'btn-danger' : 'btn-success'}`}
                onClick={() => setEnabled((v) => !v)}
              >
                {enabled ? 'Disable' : 'Enable'}
              </button>
            </div>

            {enabled && (
              <>
                <div className="form-group">
                  <label>Razorpay Key ID</label>
                  <input
                    type="text"
                    value={keyId}
                    placeholder="rzp_test_..."
                    onChange={(e) => setKeyId(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Razorpay Key Secret</label>
                  <input
                    type="password"
                    value={keySecret}
                    placeholder={hasSecret ? '•••••••• (configured)' : 'Enter key secret'}
                    onChange={(e) => setKeySecret(e.target.value)}
                  />
                  <small className={styles.hint}>Stored encrypted. Leave blank to keep the existing secret.</small>
                </div>
              </>
            )}

            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const OnlinePaymentSettings = () => {
  const [derasars, setDerasars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getSuperAdminDerasars()
      .then((res) => setDerasars(res.data || []))
      .catch((err) => toast.error(getApiErrorMessage(err, 'Failed to load derasars')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? derasars.filter((d) =>
        d.name?.toLowerCase().includes(q) ||
        d.city?.toLowerCase().includes(q))
    : derasars;

  return (
    <div>
      <div className="page-header">
        <h2><FiCreditCard style={{ marginRight: 8 }} />Online Payment Settings</h2>
        <p className={styles.subtitle}>
          Enable online payments and manage Razorpay credentials for each Derasar. The Key
          Secret is stored encrypted and never shown again.
        </p>
      </div>

      <div className={styles.searchBar}>
        <FiSearch />
        <input placeholder="Search by name or city…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <div className="card">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Derasar</th>
                <th>Location</th>
                <th>Online Payment</th>
                <th className={styles.right}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className={styles.muted}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={4} className={styles.muted}>No derasars found.</td></tr>
              ) : filtered.map((d) => (
                <tr key={d.id}>
                  <td><span className={styles.name}>{d.name}</span></td>
                  <td>{d.city || '—'}</td>
                  <td>
                    <span className={`badge ${d.onlinePaymentEnabled ? 'badge-approved' : 'badge-rejected'}`}>
                      {d.onlinePaymentEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className={styles.right}>
                    <button className="btn btn-sm btn-secondary" onClick={() => setEditing(d)}>
                      <FiSettings style={{ marginRight: 6 }} />Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <EditModal
          derasar={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

export default OnlinePaymentSettings;
